<script setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { Button, Card } from '@drama/ui'

import TaskDetailPanel from '../components/TaskDetailPanel.vue'
import TaskForestNode from '../components/TaskForestNode.vue'
import { agentsIn, api, currentGuild, session } from '../api.js'
import { buildForest } from '../task-forest.js'

// 一棵任务树：一份需求分解成的工作，以及每个节点在看板上的真实进展。数据与进展的汇总都由服务端算好，这里只负责画。
// 树属于公会：只列顶栏「当前公会」的树；别的公会的树和不存在的一样是 404。
const STATUSES = ['needs_fix', 'in_progress', 'not_started', 'accepted']

const { t, locale } = useI18n()
const route = useRoute()
const tree = ref(null)
const trees = ref([])
const error = ref('')
const notFound = ref(false)
/** 当前公会的树列表取回来了没有（没取回来之前不说「没有树」）。 */
const listed = ref(false)
/** 收起的节点 id。默认全部展开：这一页的用处就是一眼看到整棵树。 */
const collapsed = ref(new Set())
/** 展开了详情（交付物、验收、追溯）的节点 id。 */
const opened = ref(new Set())
/** 只看某一种进展的叶子；null 为全部。 */
const only = ref(null)
/** 挂在这棵树节点下面的看板任务（任务树看法），选了树时才取。 */
const forestData = ref(null)
const forest = computed(() => forestData.value === null ? null : buildForest(forestData.value.tasks))

// 当前公会定下来（登录后取到可选范围）或换了之后重新取。
watch(() => [currentGuild.slug, route.params.slug], async ([guild], _old, onCleanup) => {
  let stale = false
  onCleanup(() => { stale = true })
  tree.value = null
  forestData.value = null
  trees.value = []
  error.value = ''
  notFound.value = false
  listed.value = false
  if (guild === null) return
  const query = `?guild=${encodeURIComponent(guild)}`
  try {
    // 列表两种页面都要：没选树时是目录，选了树时是顶部的切换条。
    const [list, one, tasks] = await Promise.all([api('GET', `/api/voko/trees${query}`), route.params.slug === undefined ? null : api('GET', `/api/voko/trees/${route.params.slug}${query}`), route.params.slug === undefined ? null : api('GET', `/api/voko/task-forest${query}&tree_slug=${encodeURIComponent(route.params.slug)}`)])
    if (stale) return
    trees.value = list.trees
    listed.value = true
    if (one !== null) tree.value = one.tree
    if (tasks !== null) forestData.value = tasks
  } catch (err) {
    if (stale) return
    if (err.code === 'NOT_FOUND') notFound.value = true
    else error.value = err.message
  }
}, { immediate: true })

function appendTasks(tasks) {
  const known = new Set(forestData.value.tasks.map((task) => task.id))
  forestData.value.tasks.push(...tasks.filter((task) => !known.has(task.id)))
}
async function moreRoots() {
  if (forestData.value.next_before === null) return
  const target = forestData.value
  try {
    const page = await api('GET', `/api/voko/task-forest?guild=${encodeURIComponent(currentGuild.slug)}&tree_slug=${encodeURIComponent(tree.value.slug)}&before=${encodeURIComponent(forestData.value.next_before)}`)
    if (forestData.value !== target) return
    appendTasks(page.tasks)
    forestData.value.next_before = page.next_before
  } catch (err) { error.value = err.message }
}

/** 一个节点下有没有处于所选进展的叶子。 */
const matches = (node) => only.value === null || node.leaves[only.value] > 0

/** 把树压平成要画的行：收起的节点不展开，筛选时只留含有所选进展的分支。 */
const rows = computed(() => {
  const out = []
  const walk = (node, depth) => {
    if (!matches(node)) return
    out.push({ node, depth })
    if (!collapsed.value.has(node.id)) for (const child of node.children) walk(child, depth + 1)
  }
  if (tree.value !== null) walk(tree.value.root, 0)
  return out
})

const allIds = computed(() => {
  const ids = []
  const walk = (node) => {
    if (node.children.length > 0) ids.push(node.id)
    node.children.forEach(walk)
  }
  if (tree.value !== null) walk(tree.value.root)
  return ids
})

/** 返回一个新的集合：有就去掉，没有就加上（换新对象，界面才会更新）。 */
function toggled(set, id) {
  const next = new Set(set)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  return next
}

// —— OKR 树（tree.kind === 'okr'）：字段照飞书 OKR 表；每条 KR 由公会里的人用自己的 Agent（真实姓名）认领。
const claimer = computed(() => session.user)
/** 打开挂上来的任务用的身份：我在当前公会的第一个 Agent。 */
const forestAgent = computed(() => (currentGuild.slug === null ? undefined : agentsIn(currentGuild.slug)[0]))
/** 还没人负责的 KR 条数（表里没填负责人、也没人认领）。 */
const unclaimed = computed(() => {
  let count = 0
  const walk = (node, isRoot) => {
    if (!isRoot && node.owner === null && node.claimed_by === null) count += 1
    node.children.forEach((child) => walk(child, false))
  }
  if (tree.value !== null && tree.value.kind === 'okr') walk(tree.value.root, true)
  return count
})
const claimError = ref('')
/** 右侧显示详情的看板任务 id（和任务列表的详情是同一个面板）；null 为不显示。 */
const selectedTask = ref(null)

async function reloadTree() {
  tree.value = (await api('GET', `/api/voko/trees/${route.params.slug}?guild=${encodeURIComponent(currentGuild.slug)}`)).tree
}
/** 点节点：认领过的 KR 在右侧显示任务详情；还没人认领的没有任务，只展开节点自己的验收说明。 */
function openNode(node) {
  if (tree.value.kind === 'okr' && node.work_task !== null) selectedTask.value = node.work_task.id
  else opened.value = toggled(opened.value, node.id)
}

async function claimNode(nodeId) {
  claimError.value = ''
  try {
    const result = await api('POST', `/api/voko/work/claim-kr?guild=${encodeURIComponent(currentGuild.slug)}`, { tree_slug: tree.value.slug, node_id: nodeId })
    selectedTask.value = result.task.id
    await reloadTree()
  } catch (err) {
    claimError.value = err.message
  }
}

const percent = (value) => `${Math.round(value * 100)}%`
const day = (iso) => new Date(iso).toLocaleDateString(locale.value, { year: 'numeric', month: 'short', day: 'numeric' })
const total = (leaves) => leaves.not_started + leaves.in_progress + leaves.needs_fix + leaves.accepted
</script>

<template>
  <p v-if="error" role="alert">{{ error }}</p>
  <p v-if="notFound" role="alert">{{ t('trees.notFound') }} <router-link to="/trees">{{ t('trees.back') }}</router-link></p>
  <p v-if="currentGuild.loaded && currentGuild.slug === null" class="tree-muted">{{ t('trees.noGuild') }}</p>

  <!-- 没有指定哪棵树：列出全部 -->
  <template v-if="route.params.slug === undefined">
    <h1>{{ t('trees.title') }}</h1>
    <p class="tree-muted">{{ t('trees.intro') }}</p>
    <p v-if="listed && trees.length === 0" class="tree-muted">{{ t('trees.noTrees') }}</p>
    <Card v-for="item in trees" :key="item.slug" class="tree-card">
      <h2><router-link :to="`/trees/${item.slug}`">{{ item.title }}</router-link></h2>
      <p>{{ item.summary }}</p>
      <div class="tree-bar" :class="`is-${item.status}`" role="img" :aria-label="t('trees.progressLabel', { percent: percent(item.progress) })">
        <span :style="{ width: percent(item.progress) }" />
      </div>
      <p class="tree-muted">
        <span class="tree-status" :class="`is-${item.status}`">{{ t(`trees.status.${item.status}`) }}</span>
        · {{ percent(item.progress) }} · {{ t('trees.leafCount', { done: item.leaves.accepted, total: total(item.leaves) }) }}<template v-if="item.effort_hours !== null"> · {{ t('trees.hours', { hours: item.effort_hours }) }}</template>
      </p>
    </Card>
  </template>

  <template v-else-if="tree">
    <nav class="tree-switch" :aria-label="t('trees.switchLabel')">
      <router-link v-for="item in trees" :key="item.slug" :to="`/trees/${item.slug}`" class="tree-tab" :class="[`is-${item.status}`, { 'is-current': item.slug === tree.slug }]" :aria-current="item.slug === tree.slug ? 'page' : undefined">
        <span class="tree-dot" aria-hidden="true" />{{ item.title }} <span class="tree-muted tree-num">{{ percent(item.progress) }}</span>
      </router-link>
      <router-link to="/trees" class="tree-muted">{{ t('trees.back') }}</router-link>
    </nav>
    <h1>{{ tree.title }}</h1>
    <p>{{ tree.summary }}</p>

    <Card class="tree-card">
      <div class="tree-summary">
        <div class="tree-summary-main">
          <strong class="tree-big">{{ percent(tree.root.progress) }}</strong>
          <span class="tree-status" :class="`is-${tree.root.status}`">{{ t(`trees.status.${tree.root.status}`) }}</span>
        </div>
        <div class="tree-bar is-large" :class="`is-${tree.root.status}`" role="img" :aria-label="t('trees.progressLabel', { percent: percent(tree.root.progress) })">
          <span :style="{ width: percent(tree.root.progress) }" />
        </div>
        <p v-if="tree.kind === 'okr'" class="tree-muted">
          {{ t('trees.leafCount', { done: tree.root.leaves.accepted, total: total(tree.root.leaves) }) }} · {{ t('trees.okr.unclaimed', { n: unclaimed }) }} · {{ t('trees.decomposedAt', { date: day(tree.decomposed_at) }) }} · <a :href="tree.sources[0].path" target="_blank" rel="noopener">{{ t('trees.okr.source') }}</a>
        </p>
        <p v-else class="tree-muted">
          {{ t('trees.leafCount', { done: tree.root.leaves.accepted, total: total(tree.root.leaves) }) }} · {{ t('trees.hours', { hours: tree.root.effort_hours }) }} · {{ t('trees.budget', { points: tree.root.budget }) }} · {{ t('trees.decomposedAt', { date: day(tree.decomposed_at) }) }}
        </p>
        <p v-if="tree.kind === 'okr' && claimer === null" class="tree-muted">{{ t('trees.okr.noAgent') }}</p>
        <p v-if="claimError" role="alert">{{ claimError }}</p>
      </div>
      <!-- 图例同时是筛选：点一种进展，只看含有这种叶子的分支 -->
      <div class="tree-legend" role="group" :aria-label="t('trees.filterLabel')">
        <button v-for="status in STATUSES" :key="status" type="button" class="tree-chip" :class="[`is-${status}`, { 'is-on': only === status }]" :aria-pressed="only === status" @click="only = only === status ? null : status">
          <span class="tree-dot" aria-hidden="true" />{{ t(`trees.status.${status}`) }} <b>{{ tree.root.leaves[status] }}</b>
        </button>
        <span class="tree-legend-actions">
          <Button variant="ghost" @click="collapsed = new Set()">{{ t('trees.expandAll') }}</Button>
          <Button variant="ghost" @click="collapsed = new Set(allIds.filter((id) => id !== tree.root.id))">{{ t('trees.collapseAll') }}</Button>
        </span>
      </div>
    </Card>

    <div class="tree-split" :class="{ 'has-panel': selectedTask !== null }">
    <Card class="tree-card tree-rows">
      <div v-for="{ node, depth } in rows" :key="node.id" class="tree-row" :class="[`is-${node.status}`, `is-${node.type}`]">
        <div class="tree-line" :style="{ paddingInlineStart: `calc(${depth} * var(--drama-space-lg))` }">
          <button v-if="node.children.length > 0" type="button" class="tree-toggle" :aria-expanded="!collapsed.has(node.id)" :aria-label="t(collapsed.has(node.id) ? 'trees.expand' : 'trees.collapse', { title: node.title })" @click="collapsed = toggled(collapsed, node.id)">
            {{ collapsed.has(node.id) ? '▸' : '▾' }}
          </button>
          <span v-else class="tree-toggle" aria-hidden="true" />
          <span class="tree-dot" aria-hidden="true" />
          <button type="button" class="tree-title" :aria-expanded="opened.has(node.id)" @click="openNode(node)">
            <span class="tree-id">{{ node.id }}</span> {{ node.title }}
          </button>
          <template v-if="tree.kind === 'okr'">
            <span v-if="node.priority !== null" class="tree-tag" :class="{ 'is-warn': node.priority === 'P0' }">{{ node.priority }}</span>
          </template>
          <template v-else>
          <span v-if="node.type === 'planning_package'" class="tree-tag">{{ t('trees.type.planning_package') }}</span>
          <span v-if="node.ready" class="tree-tag is-ready">{{ t('trees.ready') }}</span>
          <span v-if="node.irreversible" class="tree-tag is-warn">{{ t('trees.irreversible') }}</span>
          <template v-if="node.posting !== null">
            <span class="tree-tag">{{ t(`trees.mode.${node.posting.mode}`) }}</span>
            <span v-if="node.task_status !== null" class="tree-tag">{{ t(`board.status.${node.task_status}`) }}</span>
            <span v-if="node.task_max_entrants !== null" class="tree-tag">{{ t('trees.entrants', { n: node.task_entry_count, max: node.task_max_entrants }) }}</span>
            <span v-if="node.task_status === 'warranty'" class="tree-tag is-warn">{{ t('trees.warrantyUntil', { at: new Date(node.task_warranty_until).toLocaleString() }) }}</span>
            <span v-if="node.postable" class="tree-tag is-ready">{{ t('trees.postable') }}</span>
            <span v-else-if="node.posting.acceptance_doc === null && node.status === 'not_started'" class="tree-tag is-warn">{{ t('trees.noAcceptanceDoc') }}</span>
          </template>
          <span v-if="node.issue !== null" class="tree-tag">#{{ node.issue }}</span>
          </template>
        </div>
        <div v-if="tree.kind === 'okr'" class="tree-facts">
          <span class="tree-status" :class="`is-${node.status}`">{{ t(`trees.status.${node.status}`) }}</span>
          <div class="tree-bar" :class="`is-${node.status}`" role="img" :aria-label="t('trees.progressLabel', { percent: percent(node.progress) })">
            <span :style="{ width: percent(node.progress) }" />
          </div>
          <span class="tree-num">{{ node.children.length === 0 && node.percent !== null ? `${node.percent}%` : percent(node.progress) }}</span>
          <template v-if="node.id !== tree.root.id">
            <span v-if="node.claimed_by !== null" class="tree-agents">{{ node.claimed_by.name }}</span>
            <span v-else-if="node.owner !== null" class="tree-agents">{{ node.owner }}</span>
            <span v-else class="tree-tag is-ready">{{ t('trees.okr.open') }}</span>
            <span v-if="node.due_on !== null" class="tree-muted">{{ t('trees.okr.due', { date: day(node.due_on) }) }}</span>
            <!-- 表里已经写了负责人的，只有同名（真实姓名）的 Agent 能认领。 -->
            <Button v-if="node.work_task === null && claimer !== null" size="sm" variant="secondary" @click="claimNode(node.id)">{{ t('okr.claim') }}</Button>
            <Button v-else-if="node.work_task !== null" size="sm" variant="ghost" @click="selectedTask = node.work_task.id">{{ t('okr.title') }} · {{ t(`okr.status.${node.work_task.status}`) }}</Button>
          </template>
        </div>
        <div v-if="tree.kind === 'okr' && opened.has(node.id)" class="tree-detail" :style="{ marginInlineStart: `calc(${depth} * var(--drama-space-lg) + var(--drama-space-xl))` }">
          <p><b>{{ t('trees.okr.criteria') }}</b></p>
          <ul v-if="node.acceptance.length > 0">
            <li v-for="item in node.acceptance" :key="item">{{ item }}</li>
          </ul>
          <p v-else class="tree-muted">{{ t('trees.okr.noCriteria') }}</p>
          <p class="tree-muted">
            {{ t('trees.okr.owner') }}：{{ node.owner ?? '—' }}
            <template v-if="node.claimed_by !== null"> · {{ t('trees.okr.claimedBy', { name: node.claimed_by.name, date: day(node.claimed_by.claimed_at) }) }}</template>
            · {{ t('trees.okr.start') }}：{{ node.start_on === null ? '—' : day(node.start_on) }}
            · {{ t('trees.okr.dueLabel') }}：{{ node.due_on === null ? '—' : day(node.due_on) }}
            · {{ t('trees.okr.done') }}：{{ node.done_on === null ? '—' : day(node.done_on) }}
          </p>
        </div>
        <div v-if="tree.kind !== 'okr'" class="tree-facts">
          <span class="tree-status" :class="`is-${node.status}`">{{ t(`trees.status.${node.status}`) }}</span>
          <template v-if="node.children.length > 0">
            <div class="tree-bar" :class="`is-${node.status}`" role="img" :aria-label="t('trees.progressLabel', { percent: percent(node.progress) })">
              <span :style="{ width: percent(node.progress) }" />
            </div>
            <span class="tree-num">{{ percent(node.progress) }}</span>
            <span class="tree-muted tree-num">{{ node.leaves.accepted }}/{{ total(node.leaves) }}</span>
          </template>
          <span class="tree-muted tree-num">{{ t('trees.hours', { hours: node.effort_hours }) }}</span>
          <span v-if="node.agents.length > 0" class="tree-agents">{{ node.agents.join('、') }}</span>
          <span v-if="node.completed_at !== null" class="tree-muted">{{ day(node.completed_at) }}</span>
          <span v-if="node.blocked_by.length > 0 && node.status !== 'accepted'" class="tree-muted">{{ t('trees.waitingOn', { ids: node.blocked_by.join(', ') }) }}</span>
        </div>
        <div v-if="tree.kind !== 'okr' && opened.has(node.id)" class="tree-detail" :style="{ marginInlineStart: `calc(${depth} * var(--drama-space-lg) + var(--drama-space-xl))` }">
          <p v-if="node.note !== null"><b>{{ t('trees.note') }}</b> {{ node.note }}</p>
          <p v-if="node.posting !== null && node.posting.acceptance_doc !== null"><b>{{ t('trees.acceptanceDoc') }}</b> <code>{{ node.posting.acceptance_doc }}</code></p>
          <p><b>{{ t('trees.deliverable') }}</b> {{ node.deliverable }}</p>
          <p><b>{{ t(node.children.length > 0 ? 'trees.integration' : 'trees.acceptance') }}</b></p>
          <ul>
            <li v-for="item in node.acceptance" :key="item">{{ item }}</li>
          </ul>
          <p class="tree-muted">
            {{ t('trees.budget', { points: node.budget }) }}
            <template v-if="node.depends_on.length > 0"> · {{ t('trees.dependsOn', { ids: node.depends_on.join(', ') }) }}</template>
            <template v-if="node.trace.length > 0"> · {{ t('trees.trace', { ids: node.trace.join(', ') }) }}</template>
          </p>
        </div>
        <div v-if="forest !== null && forestAgent !== undefined && forest.rootsUnder(tree.slug, node.id).length > 0" class="tree-forest" :style="{ marginInlineStart: `calc(${depth} * var(--drama-space-lg) + var(--drama-space-xl))` }" :data-node="node.id">
          <p class="tree-muted">{{ t('forest.attached') }}</p>
          <TaskForestNode v-for="task in forest.rootsUnder(tree.slug, node.id)" :key="task.id" :task="task" :branches-of="forest.branchesOf" :agent-id="forestAgent.id" @loaded="appendTasks" />
        </div>
      </div>
      <Button v-if="forestData !== null && forestData.next_before !== null" variant="ghost" @click="moreRoots">{{ t('okr.more') }}</Button>
      <p v-if="rows.length === 0" class="tree-muted">{{ t('trees.empty') }}</p>
    </Card>
    <TaskDetailPanel v-if="selectedTask !== null" :task-id="selectedTask" @close="selectedTask = null" @changed="reloadTree" />
    </div>
  </template>
</template>

<style scoped>
.tree-split { display: grid; gap: var(--drama-space-lg); grid-template-columns: minmax(0, 1fr); align-items: start; }
.tree-split.has-panel { grid-template-columns: minmax(0, 1fr) minmax(300px, 400px); }
@media (max-width: 1100px) {
  .tree-split.has-panel { grid-template-columns: minmax(0, 1fr); }
}
.tree-forest {
  padding: var(--drama-space-xs) 0 var(--drama-space-sm);
}

.tree-card {
  margin-bottom: var(--drama-space-md);
}
.tree-muted {
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-sm);
}
.tree-switch {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--drama-space-sm);
  margin-bottom: var(--drama-space-md);
}
.tree-tab {
  display: inline-flex;
  align-items: center;
  gap: var(--drama-space-xs);
  padding: var(--drama-space-xs) var(--drama-space-md);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-full);
  background: var(--drama-color-surface);
  color: inherit;
  text-decoration: none;
}
.tree-tab.is-current {
  border-color: var(--drama-color-primary);
  box-shadow: inset 0 0 0 1px var(--drama-color-primary);
  font-weight: var(--drama-font-weight-medium);
}
.tree-summary-main {
  display: flex;
  align-items: baseline;
  gap: var(--drama-space-md);
}
.tree-big {
  font-size: calc(var(--drama-font-size-xl) * 1.6);
  font-variant-numeric: tabular-nums;
}

/* 四种进展各一个颜色；颜色之外总有文字，不靠颜色单独传达。 */
.is-not_started { --tree-color: var(--drama-color-border-strong); }
.is-in_progress { --tree-color: var(--drama-color-warning); }
.is-needs_fix { --tree-color: var(--drama-color-danger); }
.is-accepted { --tree-color: var(--drama-color-success); }

.tree-dot {
  display: inline-block;
  flex: none;
  width: 0.7em;
  height: 0.7em;
  border-radius: var(--drama-radius-full);
  background: var(--tree-color);
}
.tree-status {
  font-size: var(--drama-font-size-sm);
  font-weight: var(--drama-font-weight-medium);
  white-space: nowrap;
}
.tree-status::before {
  content: '';
  display: inline-block;
  width: 0.6em;
  height: 0.6em;
  margin-inline-end: 0.4em;
  border-radius: var(--drama-radius-full);
  background: var(--tree-color);
}
.tree-bar {
  flex: none;
  width: 96px;
  height: 6px;
  border-radius: var(--drama-radius-full);
  background: var(--drama-color-surface-alt);
  overflow: hidden;
}
.tree-bar.is-large {
  width: 100%;
  height: 10px;
  margin-block: var(--drama-space-sm);
}
.tree-bar span {
  display: block;
  height: 100%;
  /* 进度条画的是「已验收的部分」，所以永远是绿色；节点自己的进展颜色看旁边的圆点。 */
  background: var(--drama-color-success);
}

.tree-legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--drama-space-sm);
  margin-top: var(--drama-space-md);
}
.tree-legend-actions {
  margin-inline-start: auto;
}
.tree-chip {
  display: inline-flex;
  align-items: center;
  gap: var(--drama-space-xs);
  padding: var(--drama-space-xs) var(--drama-space-sm);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-full);
  background: var(--drama-color-surface);
  color: inherit;
  font: inherit;
  font-size: var(--drama-font-size-sm);
  cursor: pointer;
}
.tree-chip.is-on {
  border-color: var(--tree-color);
  box-shadow: inset 0 0 0 1px var(--tree-color);
}

.tree-rows {
  overflow-x: auto;
  container-type: inline-size;
}
.tree-row {
  /* 宽屏一行一个节点：左边是树，右边是进展；窄屏右边折到下一行。 */
  display: grid;
  grid-template-columns: minmax(14rem, 1fr) auto;
  align-items: center;
  column-gap: var(--drama-space-md);
  border-inline-start: 4px solid var(--tree-color);
  border-bottom: 1px solid var(--drama-color-border);
  padding: var(--drama-space-xs) var(--drama-space-sm);
}
/* 按列表自己的宽度折行（有侧栏时主区比窗口窄得多）：窄了右边的进展、负责人折到标题下面。 */
@container (max-width: 760px) {
  .tree-row {
    grid-template-columns: minmax(0, 1fr);
  }
  .tree-row .tree-facts {
    justify-content: flex-start;
    padding-inline-start: var(--drama-space-xl);
  }
}
.tree-row.is-project,
.tree-row.is-control_account {
  background: var(--drama-color-surface-alt);
  font-weight: var(--drama-font-weight-medium);
}
.tree-line,
.tree-facts {
  display: flex;
  align-items: center;
  gap: var(--drama-space-sm);
}
.tree-facts {
  flex-wrap: wrap;
}
.tree-facts {
  justify-content: flex-end;
  font-weight: var(--drama-font-weight-normal);
}
.tree-facts .tree-status {
  min-width: 4.5em;
}
.tree-facts .tree-num {
  min-width: 3em;
  text-align: end;
}
.tree-toggle {
  flex: none;
  width: 1.5em;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  cursor: pointer;
}
.tree-title {
  /* 标题和前面的圆点留在同一行，太长就在自己里面折行。 */
  flex: 0 1 auto;
  min-width: 0;
  border: 0;
  padding: 0;
  background: none;
  color: inherit;
  font: inherit;
  text-align: start;
  cursor: pointer;
}
.tree-title:hover {
  text-decoration: underline;
}
.tree-id {
  color: var(--drama-color-text-muted);
  font-variant-numeric: tabular-nums;
}
.tree-tag {
  padding: 0 var(--drama-space-xs);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-sm);
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
  font-weight: var(--drama-font-weight-normal);
  white-space: nowrap;
}
.tree-tag.is-ready {
  border-color: var(--drama-color-primary);
  color: var(--drama-color-primary);
}
.tree-tag.is-warn {
  border-color: var(--drama-color-danger);
  color: var(--drama-color-danger);
}
.tree-num {
  font-variant-numeric: tabular-nums;
}
.tree-agents {
  font-size: var(--drama-font-size-sm);
}
.tree-detail {
  grid-column: 1 / -1;
  margin-block: var(--drama-space-xs);
  padding: var(--drama-space-sm);
  border-radius: var(--drama-radius-md);
  background: var(--drama-color-surface);
  font-size: var(--drama-font-size-sm);
  font-weight: var(--drama-font-weight-normal);
}
.tree-detail p,
.tree-detail ul {
  margin: 0 0 var(--drama-space-xs);
}
</style>
